/**
 * Each venture's own wordmark: the 2am logo image, or the Azerty Labs mark
 * set in the site's display face.
 */
import React from "react";
import PropTypes from "prop-types";

/** True when the venture has a custom mark (otherwise render its name). */
export const hasVentureMark = (key) => key === "azertylabs" || key === "2am";

// 2am wordmark derivatives: 480x139 (1x) and 960x277 (2x) in webp + png, cut from
// the 2156x623 originals which stay on disk for admin/other references.
const TWO_AM_MARK = { width: 480, height: 139 };
const twoAmSrcSet = (base, ext) => `${base}-480.${ext} 1x, ${base}-960.${ext} 2x`;

/**
 * The venture's wordmark, sized by `fontSize` (the image's height for 2am).
 * Returns null for ventures without a custom mark: check hasVentureMark() first.
 * @param {{ ventureKey: string, dark?: boolean, fontSize?: string }} props
 *   dark: the variant for dark slabs.
 */
const VentureMark = ({ ventureKey, dark = false, fontSize = "clamp(2rem, 4.2vw, 3.5rem)" }) => {
	if (ventureKey === "2am") {
		// "clear" is the white mark for dark slabs; "dark" is the ink mark for light backgrounds.
		const base = dark ? "/logo/2am-horizontal-clear" : "/logo/2am-horizontal-dark";
		return (
			<picture>
				<source type="image/webp" srcSet={twoAmSrcSet(base, "webp")} />
				<img
					src={`${base}-480.png`}
					srcSet={twoAmSrcSet(base, "png")}
					width={TWO_AM_MARK.width}
					height={TWO_AM_MARK.height}
					alt="2am"
					decoding="async"
					className="group-hover:opacity-70 transition-opacity duration-300"
					style={{ height: fontSize, width: "auto" }}
				/>
			</picture>
		);
	}
	if (ventureKey === "azertylabs") {
		// Two lines and the accent period from azerty-labs.com's wordmark, set in
		// the site's own display face so it does not read as a foreign brand.
		const textClass = dark ? "text-pp-bg" : "text-pp-ink";
		return (
			<span
				className={`font-sligoil font-bold ${textClass} tracking-tight inline-block group-hover:text-pp-accent transition-colors duration-300`}
				style={{ fontSize, lineHeight: 0.9 }}
			>
				Azerty
				<br />
				Labs<span className="text-pp-accent">.</span>
			</span>
		);
	}
	return null;
};

VentureMark.propTypes = {
	ventureKey: PropTypes.string.isRequired,
	dark: PropTypes.bool,
	fontSize: PropTypes.string,
};

export default VentureMark;
